import { useCallback, useEffect, useState } from 'react'
import BiometricAuthService from '../services/BiometricAuthService'
import CookiesStorageService from '../services/CookiesStorageService'

/**
 * Device biometric support and this account's enrollment, kept apart.
 *
 * `useBiometricCapability` answers "can we offer the login button", so it
 * reports available only when a vault exists. Settings needs the two facts
 * separately: which sensor the device has decides whether the row is shown at
 * all, and whether this account is enrolled decides the switch position.
 *
 * `type` is one of 'face' | 'fingerprint' - so a device with a fingerprint
 * reader shows only the fingerprint row and never the face one.
 */
export default function useBiometricSettings() {
  const [state, setState] = useState({
    loading: true, supported: false, type: null, enrolled: false, blockedBy: null,
  })

  const read = useCallback(async () => {
    try {
      const [{ available, type, notEnrolled, lockedOut }, vault] = await Promise.all([
        BiometricAuthService.isAvailable(),
        BiometricAuthService.getVaultStatus(),
      ])

      const email = CookiesStorageService.getUserEmail()
      // A vault belonging to a different account is not this user's enrollment:
      // showing it as on would let them "turn off" someone else's saved sign-in.
      const enrolled = !!vault.enrolled &&
        !!email &&
        vault.email?.toLowerCase() === email.toLowerCase()

      // Only face and fingerprint have copy a user would recognise, so any other
      // biometry the device reports is treated as unsupported.
      const knownType = type === 'face' || type === 'fingerprint'

      // A sensor the user cannot use right now still counts as supported - the
      // row is shown, switched off and disabled, saying what to do about it.
      // Only missing hardware hides it.
      const usable = knownType && (available || notEnrolled || lockedOut)

      // What to tell the user, and what the row's helper line keys off. null
      // means the row is fully operable.
      let blockedBy = null
      if (usable && !available) blockedBy = lockedOut ? 'locked-out' : 'not-enrolled'

      return {
        loading: false,
        supported: usable,
        type: usable ? type : null,
        enrolled: enrolled && available,
        blockedBy,
      }
    } catch (error) {
      return {
        loading: false, supported: false, type: null, enrolled: false, blockedBy: null,
      }
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    read().then((next) => { if (!cancelled) setState(next) })
    return () => { cancelled = true }
  }, [read])

  const refresh = useCallback(async () => {
    const next = await read()
    setState(next)
    return next
  }, [read])

  return { ...state, refresh }
}
